import { useQuery } from '@tanstack/react-query';

import { getSubscriptionCodes } from '@/services/subscription-code';
import type { CodeGuestPasses } from '@/helpers/guest-pass';
import { Subscription } from './subscription-groups';

export const SUBSCRIPTION_CODE_STATUSES = [
  'ACTIVE',
  'FULLY_USED',
  'PAUSED',
  'EXPIRED',
  'PAST_DUE',
  'CANCELED',
  'DELETED',
] as const;

export type SubscriptionCodeStatus =
  (typeof SUBSCRIPTION_CODE_STATUSES)[number];

export interface SubscriptionCode {
  subscription_code_id: string;
  order_id: string;
  subscription_id: string;
  user_id: string;
  code: string;
  classes_left: number | null;
  // Absent on a backend without waitlists: read it through getHeldCredits.
  credits_held?: number;
  expires_at: string | null;
  type: 'CUSTOM' | 'PAID';
  status: SubscriptionCodeStatus;
  has_stripe_subscription: boolean;
  current_period_end: string | null;
  cycles_consumed: number;
  // This cycle's passes.
  guest_passes?: CodeGuestPasses | null;
  paused_at: string | null;
  canceled_at: string | null;
  resumed_at: string | null;
  next_payment_attempt: string | null;
  has_pending_plan_change: boolean;
  created_at: string;
  updated_at: string;
  order: {
    order_id: string;
    total_amount: number;
    final_amount: number;
    // What a money card paid of the first payment (gift cards M1); 0 or left
    // out otherwise, and on every renewal.
    gift_card_paid?: number;
    currency: string;
    admin_user_id: string | null;
    coupon_code: string | null;
    coupon_type: 'STRIPE' | 'CUSTOM' | 'FREE' | null;
  };
  subscription: Pick<
    Subscription,
    | 'subscription_id'
    | 'subscription_group_id'
    | 'name'
    | 'total_number_of_classes_per_cycle'
    | 'charge_every_count'
    | 'charge_every_interval'
    | 'number_of_billing_cycles'
    | 'renewal_classes_behavior'
    | 'is_custom'
    | 'image'
    | 'price'
    | 'currency'
    | 'subscription_classes'
  >;
  pending_plan_change: {
    plan_change_id: string;
    change_type: 'UPGRADE' | 'DOWNGRADE';
    effective_at: string | null;
    status: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'FAILED' | 'CANCELED';
    to_subscription: Pick<
      Subscription,
      | 'subscription_id'
      | 'name'
      | 'price'
      | 'currency'
      | 'charge_every_count'
      | 'charge_every_interval'
      | 'number_of_billing_cycles'
      | 'total_number_of_classes_per_cycle'
    >;
  } | null;
}

export const getPastDueSubscriptions = (codes: SubscriptionCode[] | undefined) =>
  (codes ?? []).filter((c) => c.status === 'PAST_DUE');

export default function useSubscriptionCodes({
  userId,
  email,
}: {
  userId?: string;
  email?: string;
} = {}) {
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    queryKey: ['subscriptionCodes', userId, email],
    queryFn: () => getSubscriptionCodes({ user_id: userId, email }),
    enabled: !!userId || !!email,
    refetchOnMount: true,
  });

  return {
    data,
    isLoading,
    // A 2xx whose body lacked the expected key resolves with no data and no
    // error, so an unreadable answer has to be caught alongside a rejected one.
    hasFailedRead: !!error || (isSuccess && !data),
    error,
    refetch,
  };
}
